import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Form, Modal, Button, Container, Spinner } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import "./WorkOrderCreate.css";
import { toast } from "react-toastify";
import WorkOrderAddress from "./WorkOrderAddress";
import WorkOrderContractInfo from "./WorkOrderContractInfo";

import { useProperty } from "../../data/PropertyHook";
import WorkOrderEntry from "./entry/WorkOrderEntry";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import SpinnerButton from "../shared/Spinnerbutton";
import useLogger from "../../util/Logger";
import WorkOrderCreateWorkItemCreate from "./WorkOrderCreateWorkItemCreate";
import { useWorkItemTemplates } from "../../data/WorkItemTemplatesHook";
import { useAppointmentSlots } from "../../data/AppointmentSlotHook";
import { useWorkItemAgreements } from "../../data/WorkItemAgreementsHook";
import { useWorkItemCategories } from "../../data/WorkItemCategoriesHook";
import { useTypeOfWork } from "../../data/TypeOfWorkHook";
import { useAssets } from "../../data/AssetsHook";
import { useCategories } from "../../data/CategoriesHook";
import { useSubCategories } from "../../data/SubCategoriesHook";
import { useReporterType } from "../../data/ReporterTypeHook";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { useContractInfo } from "../../data/ContractInfoHook";
import { useProjects } from "../../data/ProjectsHook";
import { useSla } from "../../data/SlaHook";
import { isSummer } from "../../data/Months";

function WorkOrderCreate() {
  const { propertyId } = useParams();
  const propertyRepository = useProperty();
  const [property, setProperty] = useState(null);
  const [contractInfo, setContractInfo] = useState(null);
  const [showWorkItemDialog, setShowWorkItemDialog] = useState(false);
  const [validated, setValidated] = useState(false);
  const [savingWithSlaAgreement, setSavingWithSlaAgreement] = useState(false);
  const [savingWithoutSlaAgreement, setSavingWithoutSlaAgreement] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyGuid, setIdempotencyGuid] = useState();
  const workOrdersRepository = useWorkOrders();
  const logger = useLogger();
  const workItemTemplatesRepository = useWorkItemTemplates();
  const navigate = useNavigate();
  const projectsRepository = useProjects();
  const appointmentSlotsRepository = useAppointmentSlots();
  const workItemAgreementsRepository = useWorkItemAgreements();
  const workItemCategoryRepository = useWorkItemCategories();
  const typeOfWorkRepository = useTypeOfWork();
  const assetRepository = useAssets();
  const categoryRepository = useCategories();
  const subCategoryRepository = useSubCategories();
  const reporterTypeRepository = useReporterType();
  const breadcrumbRepository = useBreadcrumb();
  const contractInfoRepository = useContractInfo();
  const slaRepository = useSla();

  useEffect(() => {
    const runMe = async () => {
      try {
        logger.debug("WorkOrderCreate.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          { name: "create" },
        ]);

        const [
          retrievedProperty,
          templates,
          appointmentSlots,
          workItemAgreements,
          workItemCategories,
          types,
          reporterTypes,
          assets,
        ] = await Promise.all([
          propertyRepository.getProperty(propertyId),
          workItemTemplatesRepository.getWorkItemTemplates([
            "DomesticFieldReactive",
            "Disconnection",
            "Reconnection",
          ]),
          appointmentSlotsRepository.updateAppointmentSlots(),
          workItemAgreementsRepository.updateWorkItemAgreements(),
          workItemCategoryRepository.updateWorkItemCategories(),
          typeOfWorkRepository.updateTypeOfWorks(),
          reporterTypeRepository.updateReporterTypes(),
          assetRepository.updateAssets(propertyId),
          projectsRepository.updateProjects(),
        ]);

        setProperty(retrievedProperty);

        const defaultTemplate = templates.find(
          (t) => t.ref === "DomesticFieldReactive"
        );

        const defaultAgreement =
          workItemAgreements.find((wia) => wia.id === "WIA001") ||
          workItemAgreements[0];

        const defaultWorkItemCategory =
          workItemCategories.find((t) => t.name === "Investigation") ||
          workItemCategories[0];

        const [categories, retrievedContractInfo] = await Promise.all([
          categoryRepository.getCategories(types[0]),
          contractInfoRepository.getContractInfo(retrievedProperty.siteId),
        ]);

        const [subCategories, sla] = await Promise.all([
          await subCategoryRepository.updateSubCategories(categories[0].id),
          await slaRepository.updateSla(
            retrievedProperty.siteId,
            categories[0].id
          ),
        ]);

        const subCategory = subCategories[0]?.id || "";
        const subCategoryName = subCategories[0]?.name || "";

        setContractInfo(retrievedContractInfo);

        const poNumber = contractInfoRepository.getPurchaseOrder();

        workOrdersRepository.clearEntry(
          propertyId,
          retrievedProperty.siteId,
          retrievedProperty.siteName,
          retrievedProperty.territoryId,
          retrievedProperty.territoryName,
          retrievedProperty.customerId,
          retrievedProperty.customerName,
          retrievedProperty.redFlag,
          retrievedProperty.propertyAccessNotes,
          defaultTemplate.id,
          defaultTemplate.ref,
          appointmentSlots[0].id,
          appointmentSlots[0].name,
          defaultAgreement.id,
          defaultAgreement.name,
          defaultWorkItemCategory.id,
          defaultWorkItemCategory.name,
          types[0],
          assets[0].id,
          categories[0].id,
          subCategory,
          categories[0].name,
          subCategoryName,
          reporterTypes[0].id,
          poNumber,
          isSummer(retrievedProperty.siteName) ? sla.SummerHrs : sla.WinterHrs
        );
      } catch (error) {
        logger.error(
          "WorkOrderCreate.useEffect",
          `An error occurred while loading create work order data for property '${propertyId}' ${error}`
        );
        toast.error(
          "An error occurred while loading the create work order data. Please try again."
        );
        navigate(`/property/${propertyId}`);
      }
    };
    runMe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIdempotencyGuid(uuidv4());
  }, []);

  const saveWorkOrder = async (agreedOutsideSla) => {
    try {
      setSavingWithSlaAgreement(agreedOutsideSla);
      setSavingWithoutSlaAgreement(!agreedOutsideSla);

      const current = workOrdersRepository.getEntry();
      const revised = {
        ...current,
        agreedoutsidesla: agreedOutsideSla,
        startby: current.createdOn,
        idempotencyGuid,
      };
      workOrdersRepository.setEntry(revised);

      const workOrderCreateResult =
        await workOrdersRepository.createNewWorkOrder(revised);

      // Save work item if required
      const revisedForWorkItemCreation = {
        ...revised,
        id: workOrderCreateResult.reference,
        propertyName: workOrderCreateResult.propertyName,
        propertyNumber: workOrderCreateResult.propertyNumber,
        idempotencyGuid: `${idempotencyGuid}-workItem`,
      };
      await workOrdersRepository.createNewWorkOrderItem(
        revisedForWorkItemCreation,
        workOrderCreateResult.reference
      );

      // Route the user back to the property
      navigate(`/property/${revised.propertyId}`);

      toast.success("The work order was created successfully. ");
    } catch (err) {
      setSubmitting(false);

      logger.error(
        "WorkOrderCreate.saveWorkOrder",
        `API create work order failed ${err}`
      );

      toast.error("The work order could not be created. Please try again.");
    }

    setShowWorkItemDialog(false);
    setSavingWithSlaAgreement(false);
    setSavingWithoutSlaAgreement(false);
  };

  const handleShowWorkItemDialog = () => setShowWorkItemDialog(true);

  const handleSubmit = async (event) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    const currentOrder = workOrdersRepository.getEntry();

    if (form.checkValidity() === false) {
      form.reportValidity();
      toast.error(
        "There are errors on your form. Please check each field and try again."
      );
    } else if (
      !currentOrder.preferredAppointmentDate &&
      currentOrder.appointmentConfirmedTitle !== "Not Agreed" &&
      currentOrder.workitemtemplateref !== "Disconnection"
    ) {
      toast.error("Please select a date from the calendar.");
    } else if (currentOrder.agreedoutsidesla === false) {
      handleShowWorkItemDialog();
    } else {
      setSubmitting(true);
      saveWorkOrder(true);
    }

    setValidated(true);
  };

  const currentEntry = workOrdersRepository.getEntry();

  // We need to wait for the base data for the entry to be set (siteId, territoryId etc)
  // before we load the sub-components as they rely on this data being set. If we
  // don't check this here, the sub-components may call getEntry() before setEntry()
  // gets called, and our base data gets overwritten with nulls.
  return property && currentEntry && currentEntry.customerId && contractInfo ? (
    <Container className="coreContent">
      <h2>Create Work Order</h2>
      <WorkOrderAddress property={property} />
      <WorkOrderContractInfo contractInfo={contractInfo} />

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <div className="entry-container">
          <WorkOrderEntry mode="create" />
          <WorkOrderCreateWorkItemCreate />
        </div>

        <div className="work-order-create-buttons-container">
          <Link to={`/property/${property.id}`}>
            <Button variant="secondary">Back to property</Button>
          </Link>
          &nbsp;
          <Button type="submit" disabled={submitting}>
            Create
          </Button>
        </div>
      </Form>

      <Modal
        show={showWorkItemDialog}
        onHide={() => setShowWorkItemDialog(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Agreed Outside SLA?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Have you agreed with the customer that this appointment can be outside
          the SLA?
        </Modal.Body>
        <Modal.Footer>
          <SpinnerButton
            disabled={savingWithSlaAgreement || savingWithoutSlaAgreement}
            waiting={savingWithSlaAgreement}
            variant="secondary"
            text="Yes"
            action={() => saveWorkOrder(true)}
          />
          <SpinnerButton
            disabled={savingWithSlaAgreement || savingWithoutSlaAgreement}
            waiting={savingWithoutSlaAgreement}
            variant="primary"
            text="No"
            action={() => saveWorkOrder(false)}
          />
        </Modal.Footer>
      </Modal>
    </Container>
  ) : (
    <Container className="coreContent">
      <Spinner
        animation="border"
        role="status"
        style={{
          marginTop: "200px",
          marginLeft: "50%",
          marginBottom: "200px",
        }}
      >
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </Container>
  );
}

export default WorkOrderCreate;
